import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useCallback } from 'react'
import type {
  HistoryItem,
  IndividualDiscount,
  InsuranceAdditionalDatesModel,
  InsuranceDto,
  InsuranceParamsGridItem,
  InsuranceParamsPagedGrid,
  InsuranceParamsPagedQuery,
  InsuranceParamsVehiclePagedQuery,
  InsurerDiscount,
  ShortInsurancePagedGrid
} from 'BrokerTsLib/src/dtos'
import { InsuranceParamsType, Product } from 'BrokerTsLib/src/enums'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'

import { usePartner } from '@/context/PartnerContext'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'
import { useApp } from './useApp'

export const useGetAll = () => {
  const url = useUrl(Apis.insuranceParams)

  return useMutation({
    mutationFn: (search: InsuranceParamsVehiclePagedQuery | InsuranceParamsPagedQuery) =>
      axios.post<InsuranceParamsPagedGrid>(`${url}/GetList`, search),
  })
}

export const useGetAllShortInsurances = () => {
  const url = useUrl(Apis.insuranceParams)

  return useMutation({
    mutationFn: (search: InsuranceParamsVehiclePagedQuery) =>
      axios.post<ShortInsurancePagedGrid>(`${url}/GetShortInsuranceList`, search),
  })
}

export const useGetInsuranceParamsDiscounts = (
  paramsId: string,
  options?: GenericQueryOptions<InsurerDiscount[]>,
  onSuccess?: (data: InsurerDiscount[] | undefined) => void,
) => {
  const url = useUrl(Apis.insuranceParams)
  return useQuery({
    queryKey: ['useGetInsuranceParamsDiscounts', paramsId],
    queryFn: async () => {
      const response = await axios.get<InsurerDiscount[]>(
        `${url}/GetDiscounts?paramsId=${paramsId}`,
      )

      if (onSuccess) {
        onSuccess(response.data)
      }

      return response
    },
    ...options
  })
}
export const useGetInsurersSpecificParams = (
  paramsId: string,
  insurerId: number | null,
  options?: GenericQueryOptions<IndividualDiscount[]>,
) => {
  const url = useUrl(Apis.insuranceParams)

  return useQuery({
    queryKey: ['useGetInsurersSpecificParams', paramsId, insurerId],
    queryFn: () =>
      axios.get<IndividualDiscount[]>(
        `${url}/GetInsurersSpecificParams?paramsId=${paramsId}${insurerId ? '&insurerId=' + insurerId : ''
        }`
      ),
    ...options
  })
}

export const useCloneInsuranceParams = () => {
  const url = useUrl(Apis.insuranceParams)

  return useMutation({
    mutationFn: ({
      paramsId,
      replacement = false,
    }: {
      paramsId: string
      replacement: boolean
    }) =>
      axios.post<{ insuranceParamsId: number }>(
        `${url}/Clone?paramsId=${paramsId}&replacement=${replacement}`,
      ),
  })
}

export const useGetIpid = (paramsId: string) => {
  const url = useUrl(Apis.insuranceParams)

  return useMutation({
    mutationFn: () =>
      axios.post<{ ipidLink: string }>(`${url}/GetIpid?paramsId=${paramsId}`),
  })
}

export const useFindContractNumber = (
  contractNumber: string,
  insurerId: number,
  options?: GenericQueryOptions<{ insuranceParamsId?: number }>,
) => {
  const url = useUrl(Apis.insuranceParams)

  return useQuery({
    queryKey: ['useFindContractNumber', contractNumber, insurerId],
    queryFn: () =>
      axios.post<{ insuranceParamsId?: number }>(
        `${url}/FindContractNumber?contractNumber=${contractNumber}&insurerId=${insurerId}`,
      ),
    ...options
  })
}

export const useGetAvailableBusinessmen = (
  paramsId: string,
  options?: GenericQueryOptions<[{ id: string; fullname: string }]>,
) => {
  const url = useUrl(Apis.insuranceParams)

  return useQuery({
    queryKey: ['useGetAvailableBusinessmen', paramsId],
    queryFn: () =>
      axios.post<[{ id: string; fullname: string }]>(
        `${url}/GetAvailableBusinessmen?paramsId=${paramsId}`,
      ),
    ...options
  })
}

export const useDeleteInsuranceParams = () => {
  const url = useUrl(Apis.insuranceParams)

  return useMutation({
    mutationFn: (data: { paramIds?: string[] }) =>
      axios.post<{ success: boolean; errors?: number[] }>(
        `${url}/DeleteInsuranceParams`,
        data,
      )
  })
}

export const useGetInsurance = (
  offerId: number,
  options?: GenericQueryOptions<InsuranceDto>,
) => {
  const url = useUrl(Apis.insuranceParams)

  return useQuery({
    queryKey: ['useGetInsurance', offerId],
    queryFn: () =>
      axios.get<InsuranceDto>(`${url}/GetInsurance`, { params: { offerId } }),
    ...options
  })
}

export const useGetInsuranceAdditionalDates = (
  paramsId: number | string,
  options?: GenericQueryOptions<InsuranceAdditionalDatesModel>,
) => {
  const url = useUrl(Apis.insuranceParams)

  return useQuery({
    queryKey: ['useGetInsuranceAdditionalDates', paramsId],
    queryFn: () =>
      axios.get<InsuranceAdditionalDatesModel>(
        `${url}/GetInsuranceAdditionalDates?paramsId=${paramsId}`,
      ),
    ...options
  })
}

export const useSaveInsuranceAdditionalDates = () => {
  const url = useUrl(Apis.insuranceParams)

  return useMutation({
    mutationFn: (data: InsuranceAdditionalDatesModel) =>
      axios.post(`${url}/SaveInsuranceAdditionalDates`, data),
  })
}

export const useGetInsurances = (
  offerIds: number[],
  options?: GenericQueryOptions<InsuranceDto>,
) => {
  const url = useUrl(Apis.insuranceParams)

  return useQueries({
    queries: offerIds.map((id) => {
      return {
        queryKey: ['useGetInsurance', id],
        queryFn: () =>
          axios.get<InsuranceDto>(`${url}/GetInsurance?offerId=${id}`),
        ...options,
      }
    }),
  })
}

export const useGetHistory = (
  paramsId: string,
  options?: GenericQueryOptions<[HistoryItem]>,
) => {
  const { product } = useApp()
  const url = useUrl(Apis.insuranceParams)

  return useQuery({
    queryKey: ['useGetHistory', paramsId, product],
    queryFn: () => axios.post<[HistoryItem]>(`${url}/GetHistory?paramsId=${paramsId}`),
    ...options
  })
}

export type InsuranceParamsNavigationTargetProps = Pick<InsuranceParamsGridItem, 'type' | 'step' | 'insuranceParamsId' | 'paramsState' | 'contractNumber' | 'offerId'>

/**
 * Vrátí stabilní funkci pro sestavení cíle navigace.
 * Na rozdíl od useGetInsuranceParamsNavigationTarget ji lze volat i uvnitř callbacku –
 * např. po uložení K1 s id právě založené nabídky (id z URL v tu chvíli ještě neplatí).
 */
export const useInsuranceParamsNavigationTargetFactory = () => {
  const { product } = useApp()
  const { currentPartner } = usePartner()

  return useCallback((
    { type, step, insuranceParamsId: id, paramsState, contractNumber, offerId }: InsuranceParamsNavigationTargetProps,
    maxStep?: number,
  ) => {
    const isContracted = paramsState === 'Contracted' || !!contractNumber
    const routeStep = isContracted ? 'complete' : Math.min(step ?? 1, maxStep ?? 5)

    // Skladové pojištění má vlastní formulář; u sjednané smlouvy se jde na detail
    if (type === InsuranceParamsType.StockBefore || type === InsuranceParamsType.StockAfter) {
      if (isContracted && offerId)
        return `/leasing/case/${id}/steps/complete/${offerId}`
      return `/leasing/stock/${id}`
    }

    switch (product) {
      case Product.TravelInsurance:
        return `/travel/steps/${routeStep}/${id}`
      case Product.LiabilityInsurance:
      case Product.PropertyInsurance:
        return `/property/steps/${routeStep}/${id}`
      case Product.VehicleLeasingInsurance:
        // VWFS – přehled sjednané smlouvy běží na vlastní route s offerId v URL
        if (currentPartner?.partner === 'VWFS' && isContracted && offerId)
          return `/leasing/case/${id}/steps/complete/${offerId}`
        return `/leasing/steps/${routeStep}/${id}`
      case Product.FleetInsurance:
        return `/fleet/steps/${routeStep}/${id}`
      default:
        return `${type == 2 ? '/extra' : ''}/steps/${routeStep}/${id}`
    }
  }, [product, currentPartner?.partner])
}

export const useGetInsuranceParamsNavigationTarget = (item: InsuranceParamsNavigationTargetProps, maxStep?: number) => {
  const getNavigationTarget = useInsuranceParamsNavigationTargetFactory()

  return getNavigationTarget(item, maxStep)
}